/** PD-417/419/422: contact economics always name the currency being compared. */
export const CRM_CONTACT_ECONOMICS_FIELDS = [
  "monthly_recurring_revenue",
  "annual_revenue",
  "annual_expenses",
] as const;
export type CrmContactEconomicsField =
  (typeof CRM_CONTACT_ECONOMICS_FIELDS)[number];

export const CRM_CONTACT_ECONOMICS_CONDITIONS = [
  "is_below",
  "is_above",
  "greater_than",
  "less_than",
  "equals",
] as const;
export type CrmContactEconomicsCondition =
  (typeof CRM_CONTACT_ECONOMICS_CONDITIONS)[number];

export const isCrmContactEconomicsField = (
  field: string,
): field is CrmContactEconomicsField =>
  (CRM_CONTACT_ECONOMICS_FIELDS as readonly string[]).includes(field);

const unavailableCurrencies: readonly string[] = Object.freeze([]);
let cachedCurrencies: readonly string[] | undefined;

/** Resolve currency support on demand; unrelated domain imports need no Intl API. */
export const getCrmContactEconomicsCurrencies = (): readonly string[] => {
  if (
    typeof Intl === "undefined" ||
    typeof Intl.supportedValuesOf !== "function"
  )
    return unavailableCurrencies;
  if (cachedCurrencies) return cachedCurrencies;
  try {
    cachedCurrencies = Object.freeze(Intl.supportedValuesOf("currency"));
    return cachedCurrencies;
  } catch {
    // A runtime without currency enumeration cannot safely edit these filters.
    return unavailableCurrencies;
  }
};
// Retain fractional thresholds exactly; a threshold need not be a payable
// amount. Bound precision and magnitude before any BigInt comparison.
const amountPattern = /^-?\d{1,15}(?:\.\d{1,6})?$/u;

export type CrmContactEconomicsValue = { currency: string; amount: string };

/** Return a canonical scalar wire value, or null for any incomplete operand. */
export const normalizeCrmContactEconomicsValue = (
  condition: string,
  value: unknown,
): string | null => {
  if (
    !(CRM_CONTACT_ECONOMICS_CONDITIONS as readonly string[]).includes(
      condition,
    ) ||
    typeof value !== "string" ||
    value.length > 256
  )
    return null;
  try {
    const parsed: unknown = JSON.parse(value);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed))
      return null;
    const candidate = parsed as Record<string, unknown>;
    if (
      Object.keys(candidate).some(
        (key) => key !== "currency" && key !== "amount",
      ) ||
      typeof candidate.currency !== "string" ||
      typeof candidate.amount !== "string"
    )
      return null;
    const currency = candidate.currency.trim().toUpperCase();
    const amount = candidate.amount.trim();
    if (
      !getCrmContactEconomicsCurrencies().includes(currency) ||
      !amountPattern.test(amount)
    )
      return null;
    return JSON.stringify({ currency, amount });
  } catch {
    return null;
  }
};

export const parseCrmContactEconomicsValue = (
  condition: string,
  value: unknown,
): CrmContactEconomicsValue | null => {
  const normalized = normalizeCrmContactEconomicsValue(condition, value);
  return normalized
    ? (JSON.parse(normalized) as CrmContactEconomicsValue)
    : null;
};

/** Compare integer provider units to an exact decimal threshold, without FX or rounding. */
export const matchesCrmContactEconomicsAmount = (args: {
  actualMinor: bigint;
  actualScale: number;
  amount: string;
  condition: CrmContactEconomicsCondition;
}): boolean => {
  if (
    !Number.isInteger(args.actualScale) ||
    args.actualScale < 0 ||
    args.actualScale > 6 ||
    !amountPattern.test(args.amount)
  )
    return false;
  const negative = args.amount.startsWith("-");
  const unsigned = negative ? args.amount.slice(1) : args.amount;
  const [whole, fraction = ""] = unsigned.split(".");
  if (!whole) return false;
  const threshold = BigInt(`${negative ? "-" : ""}${whole}${fraction}`);
  const left = args.actualMinor * 10n ** BigInt(fraction.length);
  const right = threshold * 10n ** BigInt(args.actualScale);
  switch (args.condition) {
    case "is_below":
    case "less_than":
      return left < right;
    case "is_above":
    case "greater_than":
      return left > right;
    case "equals":
      return left === right;
  }
};
